import { getErc20TokenList } from '@ui/constants/tokens';
import { TOKEN_ADDRESS_BY_CHAIN } from '@ui/services/contract/constants/address';
import { ChainId } from '@ui/types/chain';
import { Token } from '@ui/types/token';
import { encodeAbiParameters, parseAbiParameters, zeroAddress } from 'viem';

import { PANCAKESWAP_V3_POOLS } from './config/dexs/pancakeswap';
import { SLVMON_POOLS } from './config/dexs/slvmon';
import { UNISWAP_POOLS } from './config/dexs/uniswap';
import { ExchangePool } from './config/types';

const POOLS: Record<ChainId, ExchangePool[]> = {
  [ChainId.MONAD_MAINNET]: [
    ...(UNISWAP_POOLS[ChainId.MONAD_MAINNET] || []),
    ...(PANCAKESWAP_V3_POOLS[ChainId.MONAD_MAINNET] || []),
  ],
  [ChainId.MONAD_TESTNET]: [
    ...(UNISWAP_POOLS[ChainId.MONAD_TESTNET] || []),
    ...(PANCAKESWAP_V3_POOLS[ChainId.MONAD_TESTNET] || []),
  ],
};

// Optional: Configure specific pools for specific pairs
// key: `${tokenIn.address}:${tokenOut.address}` (lowercase)
// value: array of pool names to use
const PAIR_SPECIFIC_CONFIG: Record<string, string[]> = {
  // Testnet USDC <-> LVUSD: Only use the 0.25% pool as known good path
  [`${TOKEN_ADDRESS_BY_CHAIN[ChainId.MONAD_TESTNET].USDC.toLowerCase()}:${TOKEN_ADDRESS_BY_CHAIN[ChainId.MONAD_TESTNET].LVUSD.toLowerCase()}`]: ['Uniswap V3 0.25%'],
  [`${TOKEN_ADDRESS_BY_CHAIN[ChainId.MONAD_TESTNET].LVUSD.toLowerCase()}:${TOKEN_ADDRESS_BY_CHAIN[ChainId.MONAD_TESTNET].USDC.toLowerCase()}`]: ['Uniswap V3 0.25%'],
  
  // LVMON <-> sLVMON
  // Removed static config for SLVMON to handle it dynamically in getExchangePools
};

export const getExchangePools = (
  chainId: ChainId,
  tokenIn?: Token,
  tokenOut?: Token,
): ExchangePool[] => {
  const allPools = POOLS[chainId] || [];

  if (!tokenIn || !tokenOut || (!tokenIn.address && !tokenIn.isNative) || (!tokenOut.address && !tokenOut.isNative)) {
    return allPools;
  }

  const tokenInAddress = tokenIn.address || zeroAddress;
  const tokenOutAddress = tokenOut.address || zeroAddress;

  // Check for SLVMON <-> LVMON pair
  const chainTokens = TOKEN_ADDRESS_BY_CHAIN[chainId];
  if (chainTokens) {
    const lvmon = chainTokens.LVMON?.toLowerCase();
    const slvmon = chainTokens.sLVMON?.toLowerCase();
    const inAddr = tokenInAddress.toLowerCase();
    const outAddr = tokenOutAddress.toLowerCase();

    if (lvmon && slvmon) {
      const isLVMONPair =
        (inAddr === lvmon && outAddr === slvmon) ||
        (inAddr === slvmon && outAddr === lvmon);
      if (isLVMONPair) {
        return [...allPools, ...(SLVMON_POOLS[chainId] || [])];
      }
    }
  }

  const key = `${tokenInAddress.toLowerCase()}:${tokenOutAddress.toLowerCase()}`;
  const specificPoolNames = PAIR_SPECIFIC_CONFIG[key];

  if (specificPoolNames) {
    return allPools.filter((pool) => specificPoolNames.includes(pool.name));
  }

  return allPools;
};

export const getExchangeTokens = (chainId: ChainId): Token[] => {
  // You can filter or add more tokens here specifically for Exchange
  return getErc20TokenList(chainId);
};

// Helper to encode data for the adapter
export const encodeAdapterData = (pool: ExchangePool, userAddress?: string): `0x${string}` => {
  if (pool.type === 'slvmon') {
    if (userAddress) {
      return encodeAbiParameters(parseAbiParameters('address'), [userAddress as `0x${string}`]);
    }
    return '0x';
  }

  if (pool.type === 'v3') {
    if (!pool.quoter || pool.fee === undefined) {
      console.warn('V3 Pool missing quoter or fee', pool);
      
return '0x';
    }
    // V3 Encoding: [router, quoter, fee]
    return encodeAbiParameters(
      parseAbiParameters('address, address, uint24'),
      [pool.router, pool.quoter, pool.fee]
    );
  }

  // V2 Encoding: [router]
  return encodeAbiParameters(
    parseAbiParameters('address'),
    [pool.router]
  );
};

export * from './config/types';
